import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Clear,
  AttachMoney,
  CalendarToday,
  Business,
  Add,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface BudgetLineItem {
  id: string;
  category: string;
  description: string;
  estimatedCost: string;
  actualCost: string;
  variance: number;
}

interface BudgetFormData {
  projectId: string;
  budgetName: string;
  budgetType: string;
  totalBudget: string;
  startDate: string;
  endDate: string;
  status: string;
  approvedBy: string;
  notes: string;
  lineItems: BudgetLineItem[];
}

const BudgetCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<BudgetFormData>({
    projectId: '',
    budgetName: '',
    budgetType: 'Project Budget',
    totalBudget: '',
    startDate: '',
    endDate: '',
    status: 'Draft',
    approvedBy: '',
    notes: '',
    lineItems: []
  });

  const [errors, setErrors] = useState<Partial<BudgetFormData>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const budgetTypes = [
    'Project Budget',
    'Phase Budget',
    'Department Budget',
    'Equipment Budget',
    'Material Budget',
    'Labor Budget',
    'Contingency Budget'
  ];

  const statusOptions = [
    'Draft',
    'Under Review',
    'Approved',
    'Active',
    'Completed',
    'Cancelled'
  ];

  const budgetCategories = [
    'Labor',
    'Materials',
    'Equipment',
    'Subcontractors',
    'Permits',
    'Insurance',
    'Utilities',
    'Transportation',
    'Overhead',
    'Contingency',
    'Other'
  ];

  const projects = [
    'Downtown Office Complex',
    'Residential Tower A',
    'Shopping Mall Renovation',
    'Highway Bridge Project',
    'School Building Extension'
  ];

  const handleInputChange = (field: keyof BudgetFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const addLineItem = () => {
    const newLineItem: BudgetLineItem = {
      id: Date.now().toString(),
      category: '',
      description: '',
      estimatedCost: '',
      actualCost: '0',
      variance: 0
    };
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newLineItem]
    }));
  };

  const removeLineItem = (id: string) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter(item => item.id !== id)
    }));
  };

  const updateLineItem = (id: string, field: keyof BudgetLineItem, value: string) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          // Calculate variance when estimated or actual cost changes
          if (field === 'estimatedCost' || field === 'actualCost') {
            const estimated = parseFloat(field === 'estimatedCost' ? value : item.estimatedCost) || 0;
            const actual = parseFloat(field === 'actualCost' ? value : item.actualCost) || 0;
            updatedItem.variance = actual - estimated;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const calculateTotalEstimated = () => {
    const lineItemsArray = Array.isArray(formData.lineItems) ? formData.lineItems : [];
    return lineItemsArray.reduce((sum, item) => sum + (parseFloat(item?.estimatedCost) || 0), 0);
  };

  const calculateTotalActual = () => {
    const lineItemsArray = Array.isArray(formData.lineItems) ? formData.lineItems : [];
    return lineItemsArray.reduce((sum, item) => sum + (parseFloat(item?.actualCost) || 0), 0);
  };

  const calculateTotalVariance = () => {
    return calculateTotalActual() - calculateTotalEstimated();
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<BudgetFormData> = {};

    if (!formData.projectId) newErrors.projectId = 'Project is required';
    if (!formData.budgetName.trim()) newErrors.budgetName = 'Budget name is required';
    if (!formData.budgetType) newErrors.budgetType = 'Budget type is required';
    if (!formData.totalBudget.trim()) newErrors.totalBudget = 'Total budget is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';

    // Validate total budget is a number
    if (formData.totalBudget && isNaN(Number(formData.totalBudget))) {
      newErrors.totalBudget = 'Total budget must be a valid number';
    }

    // Validate date range
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('loading');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Budget data:', formData);
      setSubmitStatus('success');
      
      // Navigate back to budgets list after successful creation
      setTimeout(() => {
        navigate('/budgets');
      }, 1500);
    } catch (error) {
      console.error('Error creating budget:', error);
      setSubmitStatus('error');
    }
  };

  const handleReset = () => {
    setFormData({
      projectId: '',
      budgetName: '',
      budgetType: 'Project Budget',
      totalBudget: '',
      startDate: '',
      endDate: '',
      status: 'Draft',
      approvedBy: '',
      notes: '',
      lineItems: []
    });
    setErrors({});
    setSubmitStatus('idle');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'loading': return 'info';
      default: return 'default';
    }
  };

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return 'error'; // Over budget
    if (variance < 0) return 'success'; // Under budget
    return 'default'; // On budget
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/budgets')} 
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Create New Budget
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Set up a comprehensive budget for your construction project
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Status Alert */}
      {submitStatus !== 'idle' && (
        <Alert 
          severity={getStatusColor(submitStatus) as any}
          sx={{ mb: 3 }}
          onClose={() => setSubmitStatus('idle')}
        >
          {submitStatus === 'loading' && 'Creating budget...'}
          {submitStatus === 'success' && 'Budget created successfully! Redirecting...'}
          {submitStatus === 'error' && 'Please fix the errors below and try again.'}
        </Alert>
      )}

      {/* Form */}
      <Card elevation={2}>
        <form onSubmit={handleSubmit}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'grid', gap: 4 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <FormControl fullWidth error={!!errors.projectId}>
                    <InputLabel>Project *</InputLabel>
                    <Select
                      value={formData.projectId}
                      onChange={(e) => setFormData(prev => ({ ...prev, projectId: e.target.value }))}
                      label="Project *"
                    >
                      {projects.map((project) => (
                        <MenuItem key={project} value={project}>
                          {project}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.projectId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.projectId}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Budget Name"
                    value={formData.budgetName}
                    onChange={handleInputChange('budgetName')}
                    error={!!errors.budgetName}
                    helperText={errors.budgetName}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl fullWidth error={!!errors.budgetType}>
                    <InputLabel>Budget Type *</InputLabel>
                    <Select
                      value={formData.budgetType}
                      onChange={(e) => setFormData(prev => ({ ...prev, budgetType: e.target.value }))}
                      label="Budget Type *"
                    >
                      {budgetTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.budgetType && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.budgetType}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Total Budget"
                    value={formData.totalBudget}
                    onChange={handleInputChange('totalBudget')}
                    error={!!errors.totalBudget}
                    helperText={errors.totalBudget}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={formData.startDate}
                    onChange={handleInputChange('startDate')}
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={formData.endDate}
                    onChange={handleInputChange('endDate')}
                    error={!!errors.endDate}
                    helperText={errors.endDate}
                    required
                    InputLabelProps={{ shrink: true }}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      label="Status"
                    >
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Chip 
                            label={status} 
                            size="small" 
                            color={
                              status === 'Approved' || status === 'Active' ? 'success' :
                              status === 'Under Review' ? 'warning' :
                              status === 'Cancelled' ? 'error' : 'default'
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Approved By"
                    value={formData.approvedBy}
                    onChange={handleInputChange('approvedBy')}
                    placeholder="Manager or supervisor name"
                  />
                </Box>
              </Box>

              {/* Budget Line Items */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" color="primary">
                    Budget Line Items
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addLineItem}
                  >
                    Add Line Item
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {formData.lineItems.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Estimated Cost</TableCell>
                          <TableCell align="right">Actual Cost</TableCell>
                          <TableCell align="right">Variance</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.lineItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={item.category}
                                  onChange={(e) => updateLineItem(item.id, 'category', e.target.value)}
                                  displayEmpty
                                >
                                  <MenuItem value="">
                                    <em>Select Category</em>
                                  </MenuItem>
                                  {budgetCategories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                      {category}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                value={item.description}
                                onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                                placeholder="Description"
                                sx={{ minWidth: 200 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.estimatedCost}
                                onChange={(e) => updateLineItem(item.id, 'estimatedCost', e.target.value)}
                                placeholder="0.00"
                                sx={{ width: 100 }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={item.actualCost}
                                onChange={(e) => updateLineItem(item.id, 'actualCost', e.target.value)}
                                placeholder="0.00"
                                sx={{ width: 100 }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`$${item.variance.toFixed(2)}`}
                                size="small"
                                color={getVarianceColor(item.variance)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeLineItem(item.id)}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Totals Row */}
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell colSpan={2}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Totals
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight="bold">
                              ${calculateTotalEstimated().toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight="bold">
                              ${calculateTotalActual().toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`$${calculateTotalVariance().toFixed(2)}`}
                              color={getVarianceColor(calculateTotalVariance())}
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="body1" color="text.secondary">
                      No line items added yet. Click "Add Line Item" to get started.
                    </Typography>
                  </Paper>
                )}
              </Box>

              {/* Notes */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Additional Notes
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  multiline
                  rows={4}
                  placeholder="Additional notes about the budget..."
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<Clear />}
                  disabled={submitStatus === 'loading'}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={submitStatus === 'loading'}
                >
                  {submitStatus === 'loading' ? 'Creating...' : 'Create Budget'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </form>
      </Card>
    </Box>
  );
};

export default BudgetCreate;